import { DatafeedSocket, type RawCandleRow } from './datafeedSocket'
import type { CandleData } from '../types/chartData'

/**
 * Candle loading for the Backtest chart over the datafeed WebSocket.
 *
 * Replaces the old per-day HTTP JSON path (`/datafeed/candle/{sym}/{tf}/{date}.json`),
 * which served 5-tuples with no volume, only M1/H1/D1, and needed one request per
 * calendar day — up to 61 in parallel for a single D1 window. The WebSocket serves
 * every timeframe, includes volume, and answers an arbitrary range in one frame.
 *
 * Times are unix SECONDS in both directions, so nothing is converted here.
 */

function toCandles(rows: RawCandleRow[]): CandleData[] {
  return rows.map(([time, open, high, low, close, volume]) => ({
    time,
    open,
    high,
    low,
    close,
    volume,
  }))
}

/**
 * Bars within an explicit `[fromTs, toTs]` window (both inclusive, unix seconds).
 * The server honours the range verbatim, clamped only to the symbol's available
 * data span — so a window past either end of history comes back short or empty
 * rather than erroring.
 */
export async function fetchCandleRange(
  socket: DatafeedSocket,
  symbol: string,
  timeframe: string,
  fromTs: number,
  toTs: number,
): Promise<CandleData[]> {
  if (toTs <= fromTs) return []
  const rows = await socket.requestHistory({ symbol, timeframe, from: fromTs, to: toTs })
  return toCandles(rows)
}

/**
 * The `count` newest bars at or before `toTs` — backwards pagination for
 * pan-left. Preferred over a range request when extending history, because the
 * bar count is what we actually want to bound and market gaps make the
 * equivalent time span unpredictable.
 */
export async function fetchCandlesBefore(
  socket: DatafeedSocket,
  symbol: string,
  timeframe: string,
  toTs: number,
  count: number,
): Promise<CandleData[]> {
  const rows = await socket.requestHistory({ symbol, timeframe, to: toTs, count })
  return toCandles(rows)
}
